import React, { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { User, SocketEvents } from '../types';
import VoiceChatControls from './VoiceChatControls';
import { WebRTCManager } from '../utils/webrtc';
import { useIsMobile } from '../utils/useIsMobile';
import DesktopPlazaLayout from './DesktopPlazaLayout';
import MobilePlazaLayout from './MobilePlazaLayout';
import UserSearchPanel from './UserSearchPanel';
import FavoritesPanel from './FavoritesPanel';
import CallHistoryPanel from './CallHistoryPanel';

interface Plaza3DProps {
    currentUser: User | null;
    onUsersUpdate: (users: User[]) => void;
    onBackToRoom: () => void;
}

const Plaza3D: React.FC<Plaza3DProps> = ({ currentUser, onUsersUpdate, onBackToRoom }) => {
    // レスポンシブデザイン
    const isMobile = useIsMobile(768);

    // 1:1マッチング用の状態
    const [matchedUser, setMatchedUser] = useState<User | null>(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isMatching, setIsMatching] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState<User[]>([]);
    const [isSocketConnected, setIsSocketConnected] = useState(false);
    const [matchStatus, setMatchStatus] = useState<string>('waiting');

    // WebRTC関連の状態
    const [webrtcManager, setWebrtcManager] = useState<WebRTCManager | null>(null);
    const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);

    // お気に入り関連の状態
    const [favoriteUserIds, setFavoriteUserIds] = useState<string[]>([]);

    // 通話依頼関連の状態
    const [incomingCallRequest, setIncomingCallRequest] = useState<{
        fromUserId: string;
        fromUser: User;
    } | null>(null);

    // マッチング開始/停止制御
    const [isMatchingActive, setIsMatchingActive] = useState(false);

    // 左パネルのタブ状態（PC版）
    const [leftPanelTab, setLeftPanelTab] = useState<'search' | 'favorites'>('search');

    // 通話延長関連の状態
    const [extensionSeconds, setExtensionSeconds] = useState(20);
    const [extensionRequestSent, setExtensionRequestSent] = useState(false);
    const [incomingExtensionRequest, setIncomingExtensionRequest] = useState<{
        fromUserId: string;
        seconds: number;
    } | null>(null);

    // currentUserがnullまたはpositionが未定義の場合のデフォルト値
    const safeCurrentUser = currentUser || {
        userId: '',
        username: 'Guest',
        avatar: '',
        position: { x: 0, y: 0, z: 0 },
    };

    // onUsersUpdateの参照を保持
    const onUsersUpdateRef = useRef(onUsersUpdate);
    useEffect(() => {
        onUsersUpdateRef.current = onUsersUpdate;
    }, [onUsersUpdate]);

    const socketRef = useRef<Socket<SocketEvents> | null>(null);
    const webrtcManagerRef = useRef<WebRTCManager | null>(null);
    const isMatchingRef = useRef(isMatching);
    const matchedUserRef = useRef(matchedUser);

    // 状態が変わったらrefを更新
    useEffect(() => {
        isMatchingRef.current = isMatching;
        matchedUserRef.current = matchedUser;
    }, [isMatching, matchedUser]);

    useEffect(() => {
        // Socket.io接続
        const socketUrl = 'https://voicechatapi.kasaneko.com';
        const socket = io(socketUrl, {
            autoConnect: true,
            reconnection: true,
            reconnectionDelay: 2000,
            reconnectionAttempts: 3,
            timeout: 10000,
        });
        socketRef.current = socket;

        // 接続エラーハンドリング
        socket.on('connect_error', (error) => {
            console.error('Socket接続エラー:', error.message);
            setIsSocketConnected(false);
        });

        socket.on('disconnect', (reason) => {
            console.log('Socket切断:', reason);
            setIsSocketConnected(false);
        });

        socket.on('reconnect', (attemptNumber) => {
            console.log('Socket再接続成功:', attemptNumber);
            setIsSocketConnected(true);
        });

        // 接続成功後に広場に参加
        socket.on('connect', async () => {
            console.log('Socket接続成功:', socket.id);
            setIsSocketConnected(true);

            // WebRTCマネージャーが未初期化の場合のみ初期化
            if (!webrtcManager) {
                const webrtc = new WebRTCManager(socket, safeCurrentUser.userId);

                // マッチング前でもマイクアクセスを取得（常時音声ON）
                console.log('🎤 マッチング前マイクアクセスを開始...');
                const micInitialized = await webrtc.initialize();

                if (micInitialized) {
                    console.log('✅ マイクアクセス成功 - 常時音声ON');
                    webrtcManagerRef.current = webrtc;
                    setWebrtcManager(webrtc);
                } else {
                    console.error('❌ マイクアクセス失敗');
                }
            }

            socket.emit('join-plaza', {
                userId: safeCurrentUser.userId,
                username: safeCurrentUser.username,
            });
        });

        // イベントリスナー
        socket.on('online-users', (users: User[]) => {
            console.log('オンラインユーザーイベント受信:', users);
            setOnlineUsers(users);
            onUsersUpdateRef.current(users);
        });

        socket.on('user-joined', (data: { userId: string; username: string }) => {
            console.log(`${data.username} が参加しました`);
        });

        // サーバーからのマッチング状態通知
        socket.on(
            'match-status',
            (data: {
                isMatched: boolean;
                matchedUser: User | null;
                timeRemaining: number;
                status: string;
            }) => {
                console.log('マッチング状態受信:', data);

                // 状態を同期的に更新
                if (data.isMatched && data.matchedUser) {
                    // マッチング成功
                    console.log('🎯 マッチング完了');
                    setMatchedUser(data.matchedUser);
                    setIsMatching(true);
                    setTimeRemaining(data.timeRemaining);
                    setMatchStatus('matched');

                    // 即座にボイスチャット開始（遅延なし）
                    if (socketRef.current && data.matchedUser && webrtcManager) {
                        console.log('🚀 ボイスチャット即座開始');
                        socketRef.current.emit('start-voice-chat', {
                            matchedUserId: data.matchedUser.userId,
                        });
                        setIsVoiceChatActive(true);
                    }
                } else {
                    // マッチング解除（未マッチング状態）
                    console.log('🔄 未マッチング状態');
                    setMatchedUser(null);
                    setIsMatching(false);
                    setTimeRemaining(0);
                    setMatchStatus('waiting');
                    setIsVoiceChatActive(false);
                }
            }
        );

        // サーバーからのタイマー通知
        socket.on('match-timer', (data: { timeRemaining: number }) => {
            // マッチング中のみタイマーを更新
            if (isMatchingRef.current && matchedUserRef.current) {
                console.log('タイマー更新:', data.timeRemaining);
                setTimeRemaining(data.timeRemaining);
            } else {
                console.log('タイマー更新スキップ（未マッチング）:', data.timeRemaining);
            }
        });

        // マッチング終了通知
        socket.on('match-ended', () => {
            console.log('マッチング終了 - 再マッチング開始');
            setMatchedUser(null);
            setIsMatching(false);
            setTimeRemaining(0);
            setIsVoiceChatActive(false);

            // 再マッチングを開始（サーバーが自動的に次のマッチングを行う）
            console.log('🔄 次のマッチング相手を探しています...');
        });

        socket.on('user-left', (data: { userId: string }) => {
            console.log(`ユーザー ${data.userId} が退出しました`);
            if (matchedUser && matchedUser.userId === data.userId) {
                setMatchedUser(null);
            }
        });

        // ボイスチャット開始通知
        socket.on(
            'voice-chat-started',
            (data: { roomId: string; matchedUserId: string; autoStart?: boolean }) => {
                console.log('🎉 ボイスチャット開始通知受信:', data);
                setIsVoiceChatActive(true);

                // WebRTCマネージャーがあれば即座に通話を開始
                if (webrtcManager && data.matchedUserId) {
                    console.log('🚀 WebRTC通話即座開始 - マイクは既に準備済み');
                    // 自動的に通話を開始
                    webrtcManager.startVoiceChat();
                }
            }
        );

        // ボイスチャット終了通知
        socket.on('voice-chat-ended', (data: { roomId: string }) => {
            console.log('ボイスチャット終了通知受信:', data);
            setIsVoiceChatActive(false);

            if (webrtcManager) {
                webrtcManager.endVoiceChat();
            }
        });

        // 通話依頼受信
        socket.on('call-request-received', (data: { fromUserId: string; fromUser: User }) => {
            console.log('通話依頼受信:', data);
            setIncomingCallRequest({
                fromUserId: data.fromUserId,
                fromUser: data.fromUser,
            });
        });

        // 通話延長申請受信
        socket.on('extension-request-received', (data: { fromUserId: string; seconds: number }) => {
            console.log('延長申請受信:', data);
            setIncomingExtensionRequest({
                fromUserId: data.fromUserId,
                seconds: data.seconds,
            });
        });

        // 通話延長承認/拒否通知
        socket.on('extension-approved', (data: { extensionSeconds: number }) => {
            console.log('延長承認通知:', data);
            setExtensionRequestSent(false);
            setTimeRemaining((prev) => prev + data.extensionSeconds);
        });

        socket.on('extension-rejected', () => {
            console.log('延長拒否通知');
            setExtensionRequestSent(false);
        });

        // クリーンアップ
        return () => {
            if (webrtcManagerRef.current) {
                webrtcManagerRef.current.cleanup();
            }
            if (socketRef.current) {
                // 広場から退出することを通知（オフラインになる）
                socketRef.current.emit('leave-plaza');
                socketRef.current.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeCurrentUser.userId, safeCurrentUser.username]);

    // ボイスチャット開始ハンドラー
    const handleStartVoiceChat = useCallback(() => {
        if (!socketRef.current || !matchedUser) {
            return;
        }

        socketRef.current.emit('start-voice-chat', {
            matchedUserId: matchedUser.userId,
        });
        setIsVoiceChatActive(true);
    }, [matchedUser]);

    // ボイスチャット終了ハンドラー
    const handleEndVoiceChat = useCallback(() => {
        if (!webrtcManager) {
            return;
        }

        webrtcManager.endVoiceChat();
        setIsVoiceChatActive(false);
    }, [webrtcManager]);

    // メニューに戻るハンドラー
    const handleBackToMenu = useCallback(() => {
        // 広場から退出することを通知（オフラインになる）
        if (socketRef.current) {
            socketRef.current.emit('leave-plaza');
        }

        // WebRTC接続を切断
        if (webrtcManagerRef.current) {
            webrtcManagerRef.current.cleanup();
        }

        // メニュー画面に戻る
        onBackToRoom();
    }, [onBackToRoom]);

    // お気に入り一覧を取得
    useEffect(() => {
        const loadFavorites = async () => {
            if (!safeCurrentUser.userId) {
                return;
            }

            try {
                const API_URL = 'https://voicechatapi.kasaneko.com';
                const response = await fetch(
                    `${API_URL}/api/users/favorites/${safeCurrentUser.userId}`
                );
                const data = await response.json();

                if (data.success && data.favorites) {
                    const favoriteIds = data.favorites.map((f: any) => f.userId);
                    setFavoriteUserIds(favoriteIds);
                }
            } catch (error) {
                console.error('お気に入り取得エラー:', error);
            }
        };

        loadFavorites();
    }, [safeCurrentUser.userId]);

    // お気に入り状態をチェック
    const checkIsFavorite = useCallback(
        (userId: string) => {
            return favoriteUserIds.includes(userId);
        },
        [favoriteUserIds]
    );

    // お気に入り追加/削除
    const handleFavoriteToggle = useCallback(
        async (targetUserId: string) => {
            try {
                const API_URL = 'https://voicechatapi.kasaneko.com';
                const response = await fetch(`${API_URL}/api/users/favorites/toggle`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: safeCurrentUser.userId,
                        targetUserId,
                    }),
                });

                const data = await response.json();

                if (data.success) {
                    setFavoriteUserIds(data.favoriteUserIds || []);
                } else {
                    alert(data.error || 'お気に入りの更新に失敗しました');
                }
            } catch (error) {
                console.error('お気に入り更新エラー:', error);
                alert('お気に入りの更新に失敗しました');
            }
        },
        [safeCurrentUser.userId]
    );

    // ユーザー検索結果をクリック
    const handleUserClick = useCallback((user: User) => {
        // ユーザー詳細表示（モーダルなど）は後で実装
        console.log('ユーザークリック:', user);
    }, []);

    // 通話依頼を送信
    const handleCallRequest = useCallback(
        async (targetUserId: string) => {
            if (!socketRef.current) {
                return;
            }

            socketRef.current.emit('call-request', {
                targetUserId,
                fromUserId: safeCurrentUser.userId,
            });
        },
        [safeCurrentUser.userId]
    );

    // 通話依頼可否をチェック
    const checkCanCallRequest = useCallback(
        (targetUserId: string) => {
            // 1. 相手がオンラインか
            const targetUser = onlineUsers.find((u) => u.userId === targetUserId);
            if (!targetUser || !targetUser.isOnline) {
                return false;
            }

            // 2. 相手が通話依頼を受けていないか（incomingCallRequestがない）
            if (incomingCallRequest && incomingCallRequest.fromUserId === targetUserId) {
                return false;
            }

            // 3. 相手が既にマッチング中でないか
            // TODO: より詳細なチェックが必要な場合は追加

            return true;
        },
        [onlineUsers, incomingCallRequest]
    );

    // マッチング開始/停止
    const handleStartMatching = useCallback(() => {
        if (!socketRef.current) {
            return;
        }

        setIsMatchingActive(true);
        setIsMatching(true);

        // サーバーにマッチング開始を通知
        socketRef.current.emit('start-matching', {
            userId: safeCurrentUser.userId,
            username: safeCurrentUser.username,
        });
    }, [safeCurrentUser.userId, safeCurrentUser.username]);

    const handleStopMatching = useCallback(() => {
        if (!socketRef.current) {
            return;
        }

        setIsMatchingActive(false);
        setIsMatching(false);
        setMatchedUser(null);
        setTimeRemaining(0);
        setIsVoiceChatActive(false);

        // WebRTC接続を切断
        if (webrtcManagerRef.current) {
            webrtcManagerRef.current.endVoiceChat();
        }

        // サーバーにマッチング停止を通知
        socketRef.current.emit('stop-matching');
    }, []);

    // タイマーが0になったら自動的に再マッチング開始
    useEffect(() => {
        if (timeRemaining === 0 && isMatching && matchedUser) {
            console.log('⏰ 通話時間終了 - 再マッチング開始');

            // WebRTCの接続を切断
            if (webrtcManager) {
                webrtcManager.endVoiceChat();
            }

            // 状態をリセットして「マッチング相手を探しています...」画面を表示
            setMatchedUser(null);
            setIsMatching(false);
            setIsVoiceChatActive(false);

            console.log('🔄 次のマッチング相手を探しています...');

            // サーバー側で自動的に次のマッチングが行われる
            // （広場に参加している状態なので、サーバーが自動的にマッチングを試みる）
        }
    }, [timeRemaining, isMatching, matchedUser, webrtcManager]);

    // 中央パネル（通話画面）のコンテンツ
    const centerPanel = (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial, sans-serif',
                color: '#333',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
            }}
        >
            <style>
                {`
          @media (max-width: 768px) {
            .plaza-content {
              padding: 20px !important;
              max-width: 100% !important;
            }
            
            .matched-user-card {
              padding: 20px 15px !important;
              margin: 10px !important;
            }
            
            .matched-user-avatar {
              width: 80px !important;
              height: 80px !important;
              font-size: 32px !important;
              margin-bottom: 8px !important;
            }
            
            .matched-user-name {
              font-size: 20px !important;
              margin-bottom: 10px !important;
            }
            
            .matched-user-profile {
              padding: 12px !important;
              margin-bottom: 12px !important;
            }
            
            .matched-user-profile h3 {
              font-size: 14px !important;
              margin-bottom: 8px !important;
            }
            
            .matched-user-profile div {
              font-size: 12px !important;
              line-height: 1.4 !important;
            }
            
            .time-remaining {
              font-size: 14px !important;
              margin-top: 10px !important;
            }
          }
          
          @media (max-width: 480px) {
            .plaza-content {
              padding: 15px !important;
            }
            
            .matched-user-card {
              padding: 15px 10px !important;
              margin: 5px !important;
            }
            
            .matched-user-avatar {
              width: 60px !important;
              height: 60px !important;
              font-size: 24px !important;
            }
            
            .matched-user-name {
              font-size: 18px !important;
              margin-bottom: 8px !important;
            }
            
            .matched-user-profile {
              padding: 10px !important;
              margin-bottom: 10px !important;
            }
            
            .matched-user-profile h3 {
              font-size: 12px !important;
              margin-bottom: 6px !important;
            }
            
            .matched-user-profile div {
              font-size: 11px !important;
              line-height: 1.3 !important;
            }
            
            .time-remaining {
              font-size: 12px !important;
              margin-top: 8px !important;
            }
          }
        `}
            </style>
            {/* メインコンテンツ - マッチング相手の情報 */}
            <div
                className='plaza-content'
                style={{
                    textAlign: 'center',
                    maxWidth: '600px',
                    padding: '40px',
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                {isMatching && matchedUser ? (
                    <div
                        className='matched-user-card'
                        style={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            padding: '40px 30px',
                            borderRadius: '20px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        {/* マッチング相手のアイコン */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                marginBottom: '30px',
                            }}
                        >
                            <div
                                className='matched-user-avatar'
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    borderRadius: '50%',
                                    background: (matchedUser as any).profileImage
                                        ? `url(${(matchedUser as any).profileImage}) center/cover`
                                        : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '48px',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                                    marginBottom: '15px',
                                    border: '3px solid white',
                                }}
                            >
                                {!(matchedUser as any).profileImage && '👤'}
                            </div>

                            {/* マッチング相手の名前（アイコンの下） */}
                            <h2
                                className='matched-user-name'
                                style={{
                                    margin: '0',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                }}
                            >
                                {matchedUser.username}
                            </h2>
                        </div>

                        {/* マッチング相手のプロフィール */}
                        <div
                            className='matched-user-profile'
                            style={{
                                background: 'rgba(255, 255, 255, 0.9)',
                                padding: '20px',
                                borderRadius: '15px',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                marginBottom: '20px',
                                textAlign: 'left',
                                position: 'relative',
                            }}
                        >
                            {/* お気に入りボタン（右上） */}
                            <button
                                onClick={() => handleFavoriteToggle(matchedUser.userId)}
                                style={{
                                    position: 'absolute',
                                    top: '15px',
                                    right: '15px',
                                    background: checkIsFavorite(matchedUser.userId)
                                        ? '#ff69b4'
                                        : '#ccc',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    transition: 'all 0.3s',
                                }}
                                title={
                                    checkIsFavorite(matchedUser.userId)
                                        ? 'お気に入り解除'
                                        : 'お気に入り追加'
                                }
                            >
                                {checkIsFavorite(matchedUser.userId) ? '❤️' : '🤍'}
                            </button>

                            <h3
                                style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                }}
                            >
                                プロフィール
                            </h3>
                            <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                {(matchedUser as any).gender && (
                                    <div style={{ marginBottom: '10px' }}>
                                        <strong>性別:</strong> {(matchedUser as any).gender}
                                    </div>
                                )}
                                {(matchedUser as any).hobbies && (
                                    <div style={{ marginBottom: '10px' }}>
                                        <strong>趣味:</strong> {(matchedUser as any).hobbies}
                                    </div>
                                )}
                                {(matchedUser as any).bio && (
                                    <div style={{ marginBottom: '10px' }}>
                                        <strong>自己紹介:</strong> {(matchedUser as any).bio}
                                    </div>
                                )}
                                {!(matchedUser as any).gender &&
                                    !(matchedUser as any).hobbies &&
                                    !(matchedUser as any).bio && (
                                        <div style={{ textAlign: 'center', opacity: 0.6 }}>
                                            まだ設定されていません
                                        </div>
                                    )}
                            </div>
                        </div>

                        {/* 残り時間 */}
                        <div
                            className='time-remaining'
                            style={{
                                fontSize: '18px',
                                marginTop: '15px',
                                fontWeight: 'bold',
                                color: '#ffd700',
                                marginBottom: '20px',
                            }}
                        >
                            残り時間: {timeRemaining}秒
                        </div>

                        {/* 通話延長機能 */}
                        <div
                            style={{
                                background: 'rgba(255, 255, 255, 0.9)',
                                padding: '20px',
                                borderRadius: '15px',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                marginTop: '20px',
                            }}
                        >
                            <h3
                                style={{
                                    margin: '0 0 15px 0',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                }}
                            >
                                通話延長
                            </h3>

                            {/* 延長依頼受信中 */}
                            {incomingExtensionRequest && (
                                <div
                                    style={{
                                        background: 'rgba(76, 175, 80, 0.2)',
                                        padding: '15px',
                                        borderRadius: '10px',
                                        marginBottom: '15px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                                        通話相手が{incomingExtensionRequest.seconds}
                                        秒延長を望んでいます。
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '10px',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <button
                                            onClick={() => {
                                                if (socketRef.current) {
                                                    socketRef.current.emit('extension-approve', {
                                                        fromUserId:
                                                            incomingExtensionRequest.fromUserId,
                                                        targetUserId: safeCurrentUser.userId,
                                                        seconds: incomingExtensionRequest.seconds,
                                                    });
                                                }
                                                const seconds = incomingExtensionRequest.seconds;
                                                setIncomingExtensionRequest(null);
                                                setTimeRemaining((prev) => prev + seconds);
                                            }}
                                            style={{
                                                padding: '10px 20px',
                                                background: '#4CAF50',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            承諾
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (socketRef.current) {
                                                    socketRef.current.emit('extension-reject', {
                                                        fromUserId:
                                                            incomingExtensionRequest.fromUserId,
                                                        targetUserId: safeCurrentUser.userId,
                                                    });
                                                }
                                                setIncomingExtensionRequest(null);
                                            }}
                                            style={{
                                                padding: '10px 20px',
                                                background: '#f44336',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            拒否
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* 延長申請フォーム */}
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    type='number'
                                    min='20'
                                    max='600'
                                    value={extensionSeconds}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value);
                                        if (val >= 20 && val <= 600) {
                                            setExtensionSeconds(val);
                                        }
                                    }}
                                    disabled={extensionRequestSent}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        border: '2px solid rgba(255, 255, 255, 0.3)',
                                        borderRadius: '8px',
                                        fontSize: '16px',
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        color: '#333',
                                    }}
                                />
                                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>秒</span>
                                <button
                                    onClick={() => {
                                        if (
                                            !socketRef.current ||
                                            !matchedUser ||
                                            extensionRequestSent
                                        ) {
                                            return;
                                        }

                                        setExtensionRequestSent(true);
                                        socketRef.current.emit('extension-request', {
                                            targetUserId: matchedUser.userId,
                                            fromUserId: safeCurrentUser.userId,
                                            seconds: extensionSeconds,
                                        });
                                    }}
                                    disabled={extensionRequestSent}
                                    style={{
                                        padding: '10px 20px',
                                        background: extensionRequestSent ? '#ccc' : '#4CAF50',
                                        color: '#333',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        cursor: extensionRequestSent ? 'not-allowed' : 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {extensionRequestSent ? '申請中...' : '延長申請'}
                                </button>
                            </div>
                            <div
                                style={{
                                    fontSize: '12px',
                                    color: '#666',
                                    marginTop: '5px',
                                    textAlign: 'center',
                                }}
                            >
                                最小20秒、最大600秒
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            padding: '40px 30px',
                            borderRadius: '20px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        {incomingCallRequest ? (
                            <>
                                <div
                                    style={{
                                        fontSize: '48px',
                                        marginBottom: '15px',
                                        textAlign: 'center',
                                    }}
                                >
                                    📞
                                </div>
                                <h2
                                    style={{
                                        margin: '0 0 15px 0',
                                        fontSize: '24px',
                                        textAlign: 'center',
                                    }}
                                >
                                    通話依頼があります
                                </h2>
                                <div
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                        padding: '20px',
                                        borderRadius: '15px',
                                        marginBottom: '20px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: '50%',
                                            background: incomingCallRequest.fromUser.profileImage
                                                ? `url(${incomingCallRequest.fromUser.profileImage}) center/cover`
                                                : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                                            margin: '0 auto 15px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '32px',
                                            border: '3px solid white',
                                        }}
                                    >
                                        {!incomingCallRequest.fromUser.profileImage && '👤'}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            marginBottom: '5px',
                                        }}
                                    >
                                        {incomingCallRequest.fromUser.username}
                                    </div>
                                    {incomingCallRequest.fromUser.userCode && (
                                        <div
                                            style={{
                                                fontSize: '14px',
                                                opacity: 0.9,
                                                marginBottom: '20px',
                                            }}
                                        >
                                            ID: {incomingCallRequest.fromUser.userCode}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '10px',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <button
                                            onClick={() => {
                                                if (!socketRef.current || !incomingCallRequest) {
                                                    return;
                                                }

                                                // 通話依頼を承諾してマッチング開始
                                                setIsMatchingActive(true);
                                                setIsMatching(true);
                                                setMatchedUser(incomingCallRequest.fromUser);
                                                setIncomingCallRequest(null);

                                                // サーバーにマッチング開始を通知
                                                socketRef.current.emit('start-matching', {
                                                    userId: safeCurrentUser.userId,
                                                    username: safeCurrentUser.username,
                                                });
                                            }}
                                            style={{
                                                padding: '12px 30px',
                                                background: '#4CAF50',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            承諾
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIncomingCallRequest(null);
                                            }}
                                            style={{
                                                padding: '12px 30px',
                                                background: '#f44336',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            拒否
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div
                                    style={{
                                        fontSize: '48px',
                                        marginBottom: '15px',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isMatchingActive ? '🔍' : '🎙️'}
                                </div>
                                <h2
                                    style={{
                                        margin: '0 0 20px 0',
                                        fontSize: '24px',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isMatchingActive
                                        ? 'マッチング相手を探しています...'
                                        : '通話を開始する準備ができました'}
                                </h2>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '15px',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {!isMatchingActive ? (
                                        <button
                                            onClick={handleStartMatching}
                                            style={{
                                                padding: '15px 40px',
                                                background: '#4CAF50',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '10px',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
                                            }}
                                        >
                                            開始
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleStopMatching}
                                            style={{
                                                padding: '15px 40px',
                                                background: '#f44336',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '10px',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)',
                                            }}
                                        >
                                            退出
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* デバッグ情報（開発用） */}

            {/* ボイスチャット制御 */}
            <VoiceChatControls
                webrtcManager={webrtcManager}
                isMatched={isMatching}
                matchedUser={matchedUser}
                onStartVoiceChat={handleStartVoiceChat}
                onEndVoiceChat={handleEndVoiceChat}
                timeRemaining={timeRemaining}
            />
        </div>
    );

    // レスポンシブレイアウト
    return (
        <div
            style={{
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
            }}
        >
            {/* ヘッダー */}
            <div
                style={{
                    flexShrink: 0,
                    padding: isMobile ? '10px 12px' : '15px 20px',
                    display: 'flex',
                    justifyContent: isMobile ? 'flex-start' : 'flex-end',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                }}
            >
                <button
                    onClick={handleBackToMenu}
                    style={{
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        padding: isMobile ? '8px 12px' : '10px 20px',
                        borderRadius: isMobile ? '6px' : '8px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                        transition: 'all 0.2s ease',
                    }}
                >
                    ← メニューに戻る
                </button>
            </div>

            {/* メインコンテンツ */}
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                {isMobile ? (
                    <MobilePlazaLayout
                        centerPanel={centerPanel}
                        leftSubTabs={{
                            search: (
                                <UserSearchPanel
                                    currentUser={safeCurrentUser}
                                    onFavoriteToggle={handleFavoriteToggle}
                                    onCallRequest={handleCallRequest}
                                    isFavorite={checkIsFavorite}
                                    canCallRequest={checkCanCallRequest}
                                />
                            ),
                            favorites: (
                                <FavoritesPanel
                                    currentUser={safeCurrentUser}
                                    onUserClick={handleUserClick}
                                    onFavoriteToggle={handleFavoriteToggle}
                                    onCallRequest={handleCallRequest}
                                    canCallRequest={checkCanCallRequest}
                                    isFavorite={checkIsFavorite}
                                />
                            ),
                        }}
                        rightPanel={
                            <CallHistoryPanel
                                currentUser={safeCurrentUser}
                                onUserClick={handleUserClick}
                                onFavoriteToggle={handleFavoriteToggle}
                                onCallRequest={handleCallRequest}
                                isFavorite={checkIsFavorite}
                                canCallRequest={checkCanCallRequest}
                            />
                        }
                    />
                ) : (
                    <DesktopPlazaLayout
                        centerPanel={centerPanel}
                        leftPanel={
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0' }}>
                                    <button
                                        onClick={() => setLeftPanelTab('search')}
                                        style={{
                                            flex: 1,
                                            padding: '12px',
                                            background:
                                                leftPanelTab === 'search' ? '#667eea' : '#f5f5f5',
                                            color: leftPanelTab === 'search' ? 'white' : '#667eea',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s',
                                        }}
                                    >
                                        検索
                                    </button>
                                    <button
                                        onClick={() => setLeftPanelTab('favorites')}
                                        style={{
                                            flex: 1,
                                            padding: '12px',
                                            background:
                                                leftPanelTab === 'favorites'
                                                    ? '#667eea'
                                                    : '#f5f5f5',
                                            color:
                                                leftPanelTab === 'favorites' ? 'white' : '#667eea',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s',
                                        }}
                                    >
                                        お気に入り
                                    </button>
                                </div>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    {leftPanelTab === 'search' ? (
                                        <UserSearchPanel
                                            currentUser={safeCurrentUser}
                                            onFavoriteToggle={handleFavoriteToggle}
                                            onCallRequest={handleCallRequest}
                                            isFavorite={checkIsFavorite}
                                            canCallRequest={checkCanCallRequest}
                                        />
                                    ) : (
                                        <FavoritesPanel
                                            currentUser={safeCurrentUser}
                                            onUserClick={handleUserClick}
                                            onFavoriteToggle={handleFavoriteToggle}
                                            onCallRequest={handleCallRequest}
                                            canCallRequest={checkCanCallRequest}
                                            isFavorite={checkIsFavorite}
                                        />
                                    )}
                                </div>
                            </div>
                        }
                        rightPanel={
                            <CallHistoryPanel
                                currentUser={safeCurrentUser}
                                onUserClick={handleUserClick}
                                onFavoriteToggle={handleFavoriteToggle}
                                onCallRequest={handleCallRequest}
                                isFavorite={checkIsFavorite}
                                canCallRequest={checkCanCallRequest}
                            />
                        }
                    />
                )}
            </div>

        </div>
    );
};

export default Plaza3D;
